/**
 * Session Security Manager
 * 
 * Provides:
 * 1. Auto-logout on browser/tab close (sessionStorage flag)
 * 2. Inactivity timeout (30 min) with warning dialog (5 min countdown)
 * 3. Works on desktop and mobile
 */

const SESSION_FLAG = 'session_active';
const LAST_ACTIVITY_KEY = 'last_activity';
let INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes (default; overridable by server policy)
const WARNING_DURATION = 2 * 60 * 1000; // 2 minutes warning countdown

let _warningCallback = null;
let _logoutCallback = null;
let _activityTimer = null;
let _isWarningShown = false;

// ---- Browser Close Detection ----

/**
 * Routes that do NOT require admin authentication. When the app boots on
 * one of these paths we must NEVER touch the admin token in localStorage,
 * because doing so logs the user out from any other tab where they had
 * the admin console open.
 *
 * Historical bug (2026-05-24): opening `/cumplimiento` in a new tab while
 * `/admin/*` was logged in elsewhere caused the new tab to:
 *   • see `token` in localStorage (shared across tabs)
 *   • NOT see `session_active` in sessionStorage (per-tab)
 *   • → conclude "browser was closed" and delete the token, kicking the
 *     other tab to /admin/login.
 *
 * Public paths must short-circuit out of `checkSessionOnStartup` entirely.
 */
const PUBLIC_PATH_PREFIXES = [
  '/cumplimiento',
  '/derecho-al-olvido',
  '/presupuesto/aceptar',
  '/booking',
  '/historia',
  '/firma',
  '/firmar',
  '/consentimiento',
  '/upload',
  '/mobile-upload',
  '/resultados',           // Galería pública Antes/Después
  '/klinilog',             // Landing pública de Klinilog
  '/cancel',               // Cancelación de cita
  '/manage-appointment',   // Gestión de cita
  '/review-confirm',       // Confirmación de reseña
  '/portal',               // Portal del paciente
  '/kiosk',                // Kiosco público
  '/triptico',             // Visor de tríptico
  '/d/',                   // Documentos públicos (share-link)
];

function isCurrentPathPublic() {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname || '/';
  // Root path is the public booking landing; any /admin/* is, by definition, not public.
  if (pathname === '/' || pathname === '') return true;
  if (pathname.startsWith('/admin')) return false;
  return PUBLIC_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Call on app startup. If the session flag is missing but a token exists,
 * it means the browser was closed → clear the token.
 * Returns true if session was cleared (needs re-login).
 *
 * IMPORTANT: bail out without touching anything when the current pathname
 * is a public route — public pages must never interfere with admin
 * sessions opened in other tabs.
 */
export function checkSessionOnStartup() {
  if (isCurrentPathPublic()) return false;

  // Sesión móvil de 60 días vía Face ID (solo superadmin): ligada al JWT,
  // NO a la pestaña. Al reabrir la PWA el sessionStorage se pierde, pero el
  // token debe sobrevivir — la caducidad la imponen el JWT (60 días) y la
  // revocación remota desde la consola.
  if (localStorage.getItem('mobile_passkey_session') === '1') {
    sessionStorage.setItem(SESSION_FLAG, '1');
    return false;
  }

  const hasToken = !!localStorage.getItem('token');
  const hasSession = !!sessionStorage.getItem(SESSION_FLAG);

  if (hasToken && !hasSession) {
    // Browser was closed and reopened → clear stale token
    localStorage.removeItem('token');
    return true; // session was cleared
  }
  return false;
}

/**
 * Call after successful login to mark the session as active.
 */
export function markSessionActive() {
  sessionStorage.setItem(SESSION_FLAG, '1');
  updateActivity();
}

/**
 * Call on logout to clear everything.
 */
export function clearSession() {
  sessionStorage.removeItem(SESSION_FLAG);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  stopInactivityMonitor();
}

// ---- Inactivity Monitor ----

const ACTIVITY_EVENTS = [
  'mousedown', 'mousemove', 'keydown', 'keypress',
  'touchstart', 'touchmove', 'scroll', 'click', 'wheel'
];

function updateActivity() {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  if (_isWarningShown) {
    // User interacted while warning is shown → dismiss it
    _isWarningShown = false;
    if (_warningCallback) _warningCallback(false);
  }
}

function _handleActivity() {
  if (!_isWarningShown) {
    updateActivity();
  }
}

function _checkInactivity() {
  const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
  const elapsed = Date.now() - lastActivity;
  
  if (!_isWarningShown && elapsed >= INACTIVITY_TIMEOUT) {
    // Show warning
    _isWarningShown = true;
    if (_warningCallback) _warningCallback(true);
    
    // Auto-logout after WARNING_DURATION
    setTimeout(() => {
      if (_isWarningShown) {
        _isWarningShown = false;
        if (_logoutCallback) _logoutCallback();
      }
    }, WARNING_DURATION);
  }
}

/**
 * Start monitoring user activity.
 * @param {Function} onWarning - Called with (show: boolean) when warning should appear/hide
 * @param {Function} onAutoLogout - Called when auto-logout should happen
 */
export function startInactivityMonitor(onWarning, onAutoLogout) {
  _warningCallback = onWarning;
  _logoutCallback = onAutoLogout;
  _isWarningShown = false;
  
  updateActivity();
  
  // Listen to activity events
  ACTIVITY_EVENTS.forEach(event => {
    document.addEventListener(event, _handleActivity, { passive: true });
  });
  
  // Also detect when the page becomes visible again (mobile tab switch)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      _checkInactivity();
    }
  });
  
  // Check every 30 seconds
  _activityTimer = setInterval(_checkInactivity, 30000);
}

export function stopInactivityMonitor() {
  ACTIVITY_EVENTS.forEach(event => {
    document.removeEventListener(event, _handleActivity);
  });
  if (_activityTimer) {
    clearInterval(_activityTimer);
    _activityTimer = null;
  }
  _warningCallback = null;
  _logoutCallback = null;
  _isWarningShown = false;
}

/**
 * User clicked "Continuar trabajando"
 */
export function dismissWarning() {
  _isWarningShown = false;
  updateActivity();
  if (_warningCallback) _warningCallback(false);
}

/**
 * Get the timestamp when auto-logout will happen (for countdown display).
 */
export function getAutoLogoutTime() {
  const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
  return lastActivity + INACTIVITY_TIMEOUT + WARNING_DURATION;
}

export { INACTIVITY_TIMEOUT, WARNING_DURATION };

/**
 * Override the inactivity timeout from server-side policy.
 * Called by AuthContext after fetching /api/security/auto-logout-policy.
 */
export function setInactivityTimeoutMinutes(minutes) {
  if (typeof minutes === 'number' && minutes > 0 && minutes <= 240) {
    INACTIVITY_TIMEOUT = minutes * 60 * 1000;
  }
}
